import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useUpdateDailyBudgetMutation } from "../../services/budget/budget";
import { BLUE, GREEN, ORANGE } from "../../styles/mui-theme";
import { formatCOP } from "../../utils/format";
import Button from "@mui/material/Button";
import { Alert, Box, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DailyBudget } from "../../types/types";

const SaveButtonRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "right",
  marginTop: theme.spacing(2),
}));

const COLUMN_WIDTH = "150px";

interface BudgetTableCellProps {
  cellColor?: string;
}

const BudgetTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "cellColor",
})<BudgetTableCellProps>(({ theme, cellColor }) => ({
  color: cellColor || theme.palette.text.primary,
  fontSize: theme.typography.body1.fontSize,
  width: COLUMN_WIDTH,
}));

interface Props {
  currentBudget: DailyBudget | undefined;
  date: string;
  isFetching: boolean;
}

export default function InitialBudgetSetup({ currentBudget, date, isFetching }: Props) {
  const [updateDailyBudget, { isLoading: isUpdating }] = useUpdateDailyBudgetMutation();
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);

  const cashInitialRef = React.useRef<HTMLInputElement>(null);
  const bankInitialRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (currentBudget) {
      if (cashInitialRef.current) {
        cashInitialRef.current.value = currentBudget.cash_initial.toString();
      }
      if (bankInitialRef.current) {
        bankInitialRef.current.value = currentBudget.bank_initial.toString();
      }
    } else {
      if (cashInitialRef.current) {
        cashInitialRef.current.value = "";
      }
      if (bankInitialRef.current) {
        bankInitialRef.current.value = "";
      }
    }
  }, [currentBudget]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();

    const cashInitial = cashInitialRef.current?.value || "0";
    const bankInitial = bankInitialRef.current?.value || "0";

    try {
      await updateDailyBudget({
        date,
        cash_initial: Number(cashInitial),
        bank_initial: Number(bankInitial),
      }).unwrap();
      setShowSuccess(true);
      setShowError(false);
      setIsEditMode(false);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Failed to update budget:", error);
      }
      setShowError(true);
      setShowSuccess(false);
    }
  };

  const toggleEditMode = () => {
    setIsEditMode((prev) => !prev);
    setShowSuccess(false);
    setShowError(false);
  };

  return (
    <>
      <form onSubmit={handleSave}>
        <TableContainer sx={{ p: 1, maxWidth: "750px", my: 2 }} component={Paper}>
          <Table aria-label="simple table">
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  <TableRow key="cash_row_initial">
                    <BudgetTableCell
                      component="th"
                      scope="row"
                      cellColor={BLUE}
                      sx={{ fontWeight: "bold" }}
                    >
                      Efectivo Inicial
                    </BudgetTableCell>
                    <BudgetTableCell cellColor={BLUE}>
                      <input
                        type="number"
                        ref={cashInitialRef}
                        defaultValue={currentBudget?.cash_initial?.toString() || ""}
                        disabled={!isEditMode}
                      />
                    </BudgetTableCell>
                  </TableRow>
                  <TableRow key="bank_row_initial">
                    <BudgetTableCell
                      component="th"
                      scope="row"
                      cellColor={ORANGE}
                      sx={{ fontWeight: "bold" }}
                    >
                      Banco Inicial
                    </BudgetTableCell>
                    <BudgetTableCell cellColor={BLUE}>
                      <input
                        type="number"
                        ref={bankInitialRef}
                        defaultValue={currentBudget?.bank_initial?.toString() || ""}
                        disabled={!isEditMode}
                      />
                    </BudgetTableCell>
                  </TableRow>
                  <TableRow key="total_row_initial">
                    <BudgetTableCell
                      component="th"
                      scope="row"
                      cellColor={GREEN}
                      sx={{ fontWeight: "bold" }}
                    >
                      Total Inicial
                    </BudgetTableCell>
                    <BudgetTableCell cellColor={GREEN} sx={{ fontWeight: "bold" }}>
                      {formatCOP(
                        (currentBudget?.cash_initial ?? 0) + (currentBudget?.bank_initial ?? 0),
                      )}
                    </BudgetTableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
          <SaveButtonRow>
            <Button type="button" variant={"outlined"} onClick={toggleEditMode} color={"info"}>
              {isEditMode ? "Cancelar" : "Editar"}
            </Button>
            <Button type="submit" variant={"contained"} disabled={!isEditMode || isUpdating}>
              {isUpdating ? <CircularProgress size={24} /> : "Guardar"}
            </Button>
          </SaveButtonRow>
        </TableContainer>
      </form>
      {showSuccess && (
        <Alert severity="success" variant={"outlined"} sx={{ maxWidth: "320px" }}>
          Apertura de caja exitosa
        </Alert>
      )}
      {showError && (
        <Alert severity="error" variant={"outlined"} sx={{ maxWidth: "320px" }}>
          Error: Algo salió mal durante la apertura de caja
        </Alert>
      )}
    </>
  );
}
